// /apps/patient/src/pages/appointments/AppointmentsList.tsx

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { patientApiService } from '../../services/api';
import { PageHeader } from '../../components/layout/PageHeader';
import { 
  Calendar, Clock, MapPin, Video, User, 
  ChevronRight, AlertCircle, Plus, CheckCircle,
  XCircle, Phone, Building2
} from 'lucide-react';
import { format, formatDistanceToNow, isPast, isToday, isTomorrow, parseISO } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { useOrgCalendarDisplay } from '../../hooks/useOrgCalendarDisplay';

interface Appointment {
  id: string;
  created_at: string;
  updated_at: string;
  patient_id: string;
  provider_id: string;
  organization_id: string;
  location_id?: string;
  
  // Date/time fields
  appointment_date: string;
  start_time: string;
  end_time: string;
  timezone: string;
  start_timestamp: number;
  end_timestamp: number;
  duration_minutes: number;
  
  // Appointment details
  appointment_type: 'initial_evaluation' | 'follow_up' | 'treatment' | 'consultation' | 
                   'telehealth' | 'group_class' | 'wellness' | 'procedure' | 'other';
  visit_reason?: string;
  chief_complaint?: string;
  
  // Telehealth
  is_telehealth: boolean;
  video_room_url?: string;
  
  // Status
  status: 'scheduled' | 'confirmed' | 'checked_in' | 'in_room' | 'with_provider' | 
          'completed' | 'cancelled' | 'no_show' | 'rescheduled';
  
  // Related data (populated from joins)
  provider?: {
    id: string;
    first_name: string;
    last_name: string;
    title?: string;
  };
  location?: {
    id: string;
    name: string;
    address_line1?: string;
    city?: string;
    state?: string;
  };
}

export function AppointmentsList() {
  const navigate = useNavigate();
  const calendarDisplay = useOrgCalendarDisplay();
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'upcoming' | 'past'>('upcoming');

  useEffect(() => {
    fetchAppointments();
  }, []);

  const fetchAppointments = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await patientApiService.getMyAppointments();
      
      // Sort by timestamp for accurate ordering
      const sorted = response.sort((a: Appointment, b: Appointment) => 
        a.start_timestamp - b.start_timestamp
      );
      
      setAppointments(sorted);
    } catch (err) {
      console.error('Error fetching appointments:', err);
      setError('Failed to load appointments. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Filter appointments based on current time
  const now = Date.now();
  const upcomingAppointments = appointments.filter(apt => 
    apt.start_timestamp > now && 
    !['cancelled', 'no_show', 'completed'].includes(apt.status)
  );
  
  const pastAppointments = appointments.filter(apt => 
    apt.start_timestamp <= now || 
    ['completed', 'cancelled', 'no_show'].includes(apt.status)
  );

  const displayedAppointments = activeTab === 'upcoming' ? upcomingAppointments : pastAppointments;

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'confirmed':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'cancelled':
        return <XCircle className="h-4 w-4 text-red-500" />;
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-blue-500" />;
      case 'no_show':
        return <AlertCircle className="h-4 w-4 text-orange-500" />;
      default:
        return <Clock className="h-4 w-4 text-gray-400" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmed':
        return 'bg-green-50 text-green-700 border-green-200';
      case 'cancelled':
        return 'bg-red-50 text-red-700 border-red-200';
      case 'completed':
        return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'no_show':
        return 'bg-orange-50 text-orange-700 border-orange-200';
      case 'scheduled':
        return 'bg-gray-50 text-gray-700 border-gray-200';
      default:
        return 'bg-gray-50 text-gray-600 border-gray-200';
    }
  };

  const formatAppointmentDate = (appointment: Appointment) => {
    const date = new Date(appointment.start_timestamp);
    
    if (isToday(date)) {
      return 'Today';
    } else if (isTomorrow(date)) {
      return 'Tomorrow';
    } else if (appointment.start_timestamp > now) {
      return format(date, 'EEEE, MMMM d');
    } else {
      return format(date, 'MMMM d, yyyy');
    }
  };

  const formatAppointmentTime = (appointment: Appointment) => {
    // Use the timezone from the appointment to display correct local time
    const startTime = formatInTimeZone(
      new Date(appointment.start_timestamp),
      appointment.timezone,
      'h:mm a'
    );
    const endTime = formatInTimeZone(
      new Date(appointment.end_timestamp),
      appointment.timezone,
      'h:mm a'
    );
    return `${startTime} - ${endTime}`;
  };

  const getCountdown = (appointment: Appointment) => {
    if (appointment.start_timestamp <= now) return null;
    
    const distance = formatDistanceToNow(new Date(appointment.start_timestamp), { 
      addSuffix: false 
    });
    
    return `in ${distance}`;
  };

  const getAppointmentTypeLabel = (type: string) => {
    return type.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <PageHeader title="Appointments" />
        <div className="flex items-center justify-center h-64 mt-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <PageHeader 
        title="Appointments"
        actions={
          <button
            onClick={() => navigate('/appointments/request')}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
          >
            <Plus className="h-4 w-4" />
            Request
          </button>
        }
      />

      {/* Tabs */}
      <div className="mt-6">
        <div className="bg-white rounded-lg p-1 flex gap-1">
          <button
            onClick={() => setActiveTab('upcoming')}
            className={`flex-1 py-2.5 px-4 rounded-md font-medium transition-colors ${
              activeTab === 'upcoming'
                ? 'bg-primary text-white'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Upcoming ({upcomingAppointments.length})
          </button>
          <button
            onClick={() => setActiveTab('past')}
            className={`flex-1 py-2.5 px-4 rounded-md font-medium transition-colors ${
              activeTab === 'past'
                ? 'bg-primary text-white'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Past ({pastAppointments.length})
          </button>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="mx-4 mt-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-red-600 mt-0.5" />
          <div>
            <p className="text-red-800">{error}</p>
            <button
              onClick={fetchAppointments}
              className="text-red-600 underline text-sm mt-1"
            >
              Try again
            </button>
          </div>
        </div>
      )}

      {/* Appointments List */}
      <div className="px-4 py-4 space-y-3">
        {displayedAppointments.length === 0 ? (
          <div className="bg-white rounded-lg p-8 text-center">
            <Calendar className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">
              {activeTab === 'upcoming' 
                ? 'No upcoming appointments' 
                : 'No past appointments'}
            </p>
            {activeTab === 'upcoming' && (
              <button
                onClick={() => navigate('/appointments/request')}
                className="mt-4 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
              >
                Request Appointment
              </button>
            )}
          </div>
        ) : (
          displayedAppointments.map((appointment) => (
            <div
              key={appointment.id}
              onClick={() => navigate(`/appointments/${appointment.id}`)}
              className="bg-white rounded-lg p-4 shadow-sm border border-gray-200 hover:shadow-md transition-shadow cursor-pointer"
            >
              {/* Date and Status Row */}
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-gray-900">
                      {formatAppointmentDate(appointment)}
                    </h3>
                    {activeTab === 'upcoming' && getCountdown(appointment) && (
                      <span className="text-sm text-primary font-medium">
                        ({getCountdown(appointment)})
                      </span>
                    )}
                  </div>
                  <p className="text-gray-600 mt-0.5">
                    {formatAppointmentTime(appointment)}
                  </p>
                </div>
                <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${getStatusColor(appointment.status)}`}>
                  {getStatusIcon(appointment.status)}
                  <span className="capitalize">
                    {appointment.status.replace('_', ' ')}
                  </span>
                </div>
              </div>

              {/* Appointment Type */}
              <div className="mb-3">
                <p className="text-sm font-medium text-gray-700">
                  {getAppointmentTypeLabel(appointment.appointment_type)}
                </p>
                {!calendarDisplay.hide_visit_reason && appointment.visit_reason && (
                  <p className="text-sm text-gray-600 mt-0.5">
                    {appointment.visit_reason}
                  </p>
                )}
              </div>

              {/* Provider and Location */}
              <div className="space-y-2">
                {appointment.provider && (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <User className="h-4 w-4 text-gray-400" />
                    <span>
                      {appointment.provider.title && `${appointment.provider.title} `}
                      {appointment.provider.first_name} {appointment.provider.last_name}
                    </span>
                  </div>
                )}

                {appointment.is_telehealth ? (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Video className="h-4 w-4 text-gray-400" />
                    <span>Telehealth Visit</span>
                  </div>
                ) : appointment.location ? (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <MapPin className="h-4 w-4 text-gray-400" />
                    <span>
                      {appointment.location.name}
                      {appointment.location.city && `, ${appointment.location.city}`}
                    </span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Building2 className="h-4 w-4 text-gray-400" />
                    <span>In-Office Visit</span>
                  </div>
                )}
              </div>

              {/* Action Buttons for Upcoming Appointments */}
              {activeTab === 'upcoming' && appointment.status !== 'cancelled' && (
                <div className="mt-4 pt-3 border-t border-gray-100">
                  <div className="flex gap-2">
                    {appointment.is_telehealth && appointment.video_room_url && 
                     isToday(new Date(appointment.start_timestamp)) && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          window.open(appointment.video_room_url, '_blank');
                        }}
                        className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
                      >
                        <Video className="h-4 w-4" />
                        Join Video
                      </button>
                    )}
                    {!appointment.is_telehealth && appointment.location && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          // Would integrate with maps API
                          const address = `${appointment.location?.address_line1}, ${appointment.location?.city}, ${appointment.location?.state}`;
                          window.open(`https://maps.google.com/?q=${encodeURIComponent(address)}`, '_blank');
                        }}
                        className="flex-1 flex items-center justify-center gap-2 px-3 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        <MapPin className="h-4 w-4" />
                        Directions
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Navigation Arrow */}
              <div className="absolute right-4 top-1/2 -translate-y-1/2">
                <ChevronRight className="h-5 w-5 text-gray-400" />
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}